<template>
  <div v-if="record" class="custom-object-inline">
    <span class="object-icon">
      <span v-if="typeof icon === 'string'" class="object-emoji">
        {{ icon }}
      </span>
      <component :is="icon" v-else />
    </span>
    <span class="object-title">{{ title }}</span>
    <span v-if="contextUrl" class="object-context" @click.stop>
      <ContextButton :url="contextUrl" size="xs" :tabindex="-1" />
    </span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRef } from "vue";

import type {
  GraphDatabase,
  ObjectIdRaw,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { getTypeIcon } from "@/components/databases";
import ContextButton from "@/components/elements/ContextButton.vue";
import { findContextField } from "@/components/graph/context";
import {
  asGraphRecordResult,
  asString,
  unwrapGraphObjectGetResult,
} from "@/components/graph/result";
import {
  displayTitle,
  extractTitleFromGraphRow,
} from "@/components/graph/title";
import * as inject from "@/components/inject";
import { releasableRef, useReleaser } from "@/ref";
import { nonReactiveIcon } from "@/utils/icon";

export type CustomObjectInlineTarget = ObjectIdRaw & {
  workspace_id: WorkspaceId;
};

export default defineComponent({
  name: "CustomObjectInline",
  components: { ContextButton },
  props: {
    target: {
      type: Object as PropType<CustomObjectInlineTarget>,
      required: true,
    },
  },
  async setup(props) {
    const target = toRef(props, "target");
    const controller = inject.controller();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const releaser = useReleaser();

    const objectId = await controller.graph.object.id.make(target.value);
    const knownDatabases =
      workspaceDatabases.value?.[target.value.workspace_id];
    const knownDatabase =
      knownDatabases?.[target.value.table_id] ??
      Object.values(knownDatabases ?? {}).find(
        (database) => database.id === target.value.table_id
      );

    const objectDataPromise = releaser.add(
      controller.graph.object
        .get(objectId)
        .then((raw) => raw.map(unwrapGraphObjectGetResult))
    );
    const databasePromise = releaser.add(
      knownDatabase
        ? Promise.resolve(releasableRef<GraphDatabase | null>(knownDatabase))
        : controller.graph.database.ofObject(objectId)
    );
    const [objectData, database] = await Promise.all([
      objectDataPromise,
      databasePromise,
    ]);

    const record = computed(() =>
      objectData.value === null
        ? undefined
        : asGraphRecordResult(objectData.value)
    );
    const title = computed(() =>
      displayTitle(
        record.value
          ? extractTitleFromGraphRow(database.value, record.value)
          : undefined
      )
    );
    const icon = computed(() =>
      nonReactiveIcon(getTypeIcon(profile.value, database.value))
    );
    const contextUrl = computed(() => {
      const field = database.value
        ? findContextField(database.value)
        : undefined;
      return field && record.value
        ? asString(record.value[field] ?? null)
        : undefined;
    });
    return { contextUrl, icon, record, title };
  },
});
</script>

<style scoped lang="scss">
.custom-object-inline {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 17px;
  padding: 0;
  border: 0;
  column-gap: 4px;
  background: transparent;
  text-align: start;

  .object-icon {
    @extend %font-small-regular;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    @include themed() {
      color: t($grey-50);
    }

    :deep(svg),
    :deep(img) {
      width: 12px;
      height: 12px;
    }
  }

  .object-title {
    @extend %font-small-regular;
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    @include themed() {
      color: t($grey-60);
    }
  }

  .object-context {
    flex: 0 0 auto;
  }
}
</style>
